import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  NotificationsActive as ReminderIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  CheckCircleOutline as DoneIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  MenuBook as JournalIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "./AddressbookPanelShell";
import Gate from "../../../components/common/Gate";
import { useToggle } from "../../../contexts/FeatureToggleContext";
import { useLayout } from "../../../contexts/LayoutContext";
import { SELECT_ENTITY_JOURNAL_EVENT } from "../../../utils/constants";
import ReminderCreateDialog, { getReminderContextDefaults } from "../../../components/reminders/ReminderCreateDialog";
import { apiGetJournals, apiDeleteJournal, apiUpdateJournal, apiCancelJournalReminder } from "../../../graphQL/journal";
import { apiGetContactsCollection } from "../../../graphQL/addressbook/collections/contacts";
import { apiGetPropertyAsset } from "../../../graphQL/properties/collections/propertyAssets";

/* ── helpers ── */

const ACTION_OPTIONS = [
  { value: "call", label: "call" },
  { value: "email", label: "email" },
  { value: "text", label: "text" },
  { value: "schedule_meeting", label: "schedule a meeting" },
  { value: "attend_meeting", label: "attend a meeting" },
  { value: "meet_in_person", label: "meet in person" },
];

const STATE_CHIP_STYLES = {
  created:   { borderColor: "#CBD5E1", color: "#475569", background: "#F8FAFC" },
  scheduled: { borderColor: "#F59E0B", color: "#B45309", background: "#FFFBEB" },
  due:       { borderColor: "#22C55E", color: "#166534", background: "#ECFDF5" },
};

function formatTimestamp(value) {
  if (value === null || value === undefined || value === "") return "";
  const asString = String(value);
  const isEpochMs = /^\d{10,}$/.test(asString);
  const d = new Date(isEpochMs ? Number(asString) : value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/* ── Reminder Card ── */

function ReminderCard({ reminder, onDelete, onMarkDone, selected, onSelect, onUpdate, refLabel, onOpenEntity, onCompleteWithNote, onAction }) {
  const rawData = reminder.data;
  const data = typeof rawData === "string" ? (() => { try { return JSON.parse(rawData); } catch { return {}; } })() : (rawData || {});
  const { status } = reminder;
  const [editingDetails, setEditingDetails] = useState(false);
  const [detailsText, setDetailsText] = useState(data?.text || "");
  const [editingDate, setEditingDate] = useState(false);
  const [dateValue, setDateValue] = useState(() => {
    if (!reminder?.dueDate) return "";
    const d = new Date(reminder.dueDate);
    if (Number.isNaN(d.getTime())) return "";
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const isDone = ["done", "completed"].includes(String(data?.status || "").trim().toLowerCase());
  const isTriggered = (status === "triggered" || reminder?.state === "due") && !isDone;
  const actionLabel = ACTION_OPTIONS.find((a) => a.value === data?.action)?.label || data?.action || "";
  const rawState = String(reminder?.state || "").toLowerCase();
  const stateValue = rawState === "active" ? "scheduled" : (rawState || "created");
  const displayState = (isTriggered || stateValue === "due") ? "due" : stateValue;
  const chipStyle = STATE_CHIP_STYLES[displayState] || STATE_CHIP_STYLES.created;
  const dateLabel = formatTimestamp(reminder?.dueDate || reminder?.timestamp);
  const doneStyle = {};

  // Association: prefer the stamped contactName, fall back to the label the
  // panel resolved from the reminder's ref (contacts/propertyAssets). A
  // reminder only reads as "global" when its ref really is global.
  const refCollectionKind = reminder?.ref?.collection || "";
  const isGlobalRef = refCollectionKind === GLOBAL_REF_COLLECTION;
  const contactName = data?.contactName || data?.refLabel || (!isGlobalRef ? refLabel : "") || "";
  const contactEmail = data?.contactEmail || "";
  const contactPhone = data?.contactPhone || "";

  const canEditDate = useToggle("reminders.card.editDate");
  const canEditDetails = useToggle("reminders.card.editDetails");

  return (
    <Box onClick={() => onSelect && onSelect(reminder)} sx={{
      border: "1px solid", borderRadius: 1.5, p: 1.25, backgroundColor: "#fff", cursor: "pointer",
      borderColor: selected ? "#7c3aed" : isTriggered ? "#fbbf24" : isDone ? "#E5E7EB" : "#d1d5db",
      ...(selected ? { boxShadow: "0 0 0 2px rgba(124,58,237,0.25)", backgroundColor: "#faf5ff" } : {}),
      ...(isTriggered && !selected ? { boxShadow: "0 2px 8px rgba(251,191,36,0.15)", backgroundColor: "#fffdf5" } : {}),
      "&:hover": { borderColor: selected ? "#7c3aed" : "#9CA3AF" },
    }}>
      {/* Top row: state chip + date + contact name + delete */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <Box sx={{
          display: "inline-flex", alignItems: "center", px: 1, py: 0.125,
          borderRadius: "999px", border: "1px solid", fontSize: 11, fontWeight: 700,
          ...chipStyle, textTransform: "capitalize", whiteSpace: "nowrap", flexShrink: 0,
        }}>
          {displayState === "due" && <ReminderIcon sx={{ fontSize: 12, mr: 0.25, color: "#166534" }} />}
          {displayState === "due" ? "Due" : displayState}
        </Box>
        {editingDate ? (
          <input
            type="datetime-local"
            autoFocus
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            onBlur={() => {
              setEditingDate(false);
              if (dateValue && onUpdate) {
                const newDue = new Date(dateValue).toISOString();
                if (newDue !== reminder.dueDate) {
                  onUpdate(reminder.id, { dueDate: newDue });
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
              if (e.key === "Escape") setEditingDate(false);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 12, border: "1px solid #c4b5fd", borderRadius: 4, padding: "2px 6px", flex: 1, outline: "none" }}
          />
        ) : (
          <Typography
            onDoubleClick={canEditDate ? (e) => { e.stopPropagation(); setEditingDate(true); } : undefined}
            sx={{ fontSize: 13, fontWeight: 600, color: "#312e81", flex: 1, cursor: "default", ...doneStyle }}
          >
            {dateLabel || "No date set"}
            {isGlobalRef && !contactName && (
              <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", ml: 0.75, px: 0.75, py: 0.125, borderRadius: "999px", border: "1px solid #CBD5E1", backgroundColor: "#F8FAFC", verticalAlign: "middle" }}>
                Global
              </Typography>
            )}
            {contactName && (
              <>
                <Typography component="span" sx={{ fontSize: 13, color: "#475569", ...doneStyle }}> — </Typography>
                {data?.action === "call" && contactPhone ? (
                  <Gate flag="reminders.card.call">
                  <Button size="small" component="span" startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onAction?.(reminder, "call", { phone: contactPhone, email: contactEmail, name: contactName }); }}
                    sx={{
                      minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                      fontSize: 11, fontWeight: 700, color: "#047857", backgroundColor: "#ECFDF5",
                      border: "1px solid #A7F3D0", "& .MuiButton-startIcon": { mr: 0.3 },
                      "&:hover": { backgroundColor: "#D1FAE5" }, verticalAlign: "middle", mx: 0.5,
                    }}>
                    Call
                  </Button>
                  </Gate>
                ) : data?.action === "text" && contactPhone ? (
                  <Gate flag="reminders.card.text">
                  <Button size="small" component="span" startIcon={<SmsIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onAction?.(reminder, "text", { phone: contactPhone, email: contactEmail, name: contactName }); }}
                    sx={{
                      minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                      fontSize: 11, fontWeight: 700, color: "#1E40AF", backgroundColor: "#EFF6FF",
                      border: "1px solid #93C5FD", "& .MuiButton-startIcon": { mr: 0.3 },
                      "&:hover": { backgroundColor: "#DBEAFE" }, verticalAlign: "middle", mx: 0.5,
                    }}>
                    Text
                  </Button>
                  </Gate>
                ) : data?.action === "email" && contactEmail ? (
                  <Gate flag="reminders.card.email">
                  <Button size="small" component="span" startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); onAction?.(reminder, "email", { phone: contactPhone, email: contactEmail, name: contactName }); }}
                    sx={{
                      minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
                      fontSize: 11, fontWeight: 700, color: "#1E40AF", backgroundColor: "#EFF6FF",
                      border: "1px solid #93C5FD", "& .MuiButton-startIcon": { mr: 0.3 },
                      "&:hover": { backgroundColor: "#DBEAFE" }, verticalAlign: "middle", mx: 0.5,
                    }}>
                    Email
                  </Button>
                  </Gate>
                ) : actionLabel ? (
                  <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: "#312e81", mx: 0.5, ...doneStyle }}>{actionLabel}</Typography>
                ) : null}
                <Typography
                  component="span"
                  onClick={onOpenEntity ? (e) => { e.stopPropagation(); onOpenEntity(reminder); } : undefined}
                  title={onOpenEntity ? "Open journal" : undefined}
                  sx={{
                    fontSize: 13, color: "#475569", ...doneStyle,
                    ...(onOpenEntity ? { cursor: "pointer", "&:hover": { color: "#3730a3", textDecoration: "underline" } } : {}),
                  }}
                >
                  {contactName}
                </Typography>
              </>
            )}
          </Typography>
        )}
        {!isDone && isTriggered && onCompleteWithNote && (
          <Button
            size="small"
            startIcon={<JournalIcon sx={{ fontSize: 13 }} />}
            onClick={(e) => { e.stopPropagation(); onCompleteWithNote(reminder); }}
            title="Mark done and open the journal to log what happened"
            sx={{
              minWidth: "auto", height: 20, px: 0.75, borderRadius: "999px", textTransform: "none",
              fontSize: 11, fontWeight: 700, color: "#92400E", backgroundColor: "#FFFBEB",
              border: "1px solid #FDE68A", "& .MuiButton-startIcon": { mr: 0.3 },
              "&:hover": { backgroundColor: "#FEF3C7" }, flexShrink: 0,
            }}
          >
            Done + note
          </Button>
        )}
        {!isDone && (
          <Gate flag="reminders.card.markDone">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMarkDone(); }} title="Mark as done" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#10b981" }, flexShrink: 0 }}>
            <DoneIcon sx={{ fontSize: 14 }} />
          </IconButton>
          </Gate>
        )}
        <Gate flag="reminders.card.delete">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Delete" sx={{ color: "#9CA3AF", p: 0.25, "&:hover": { color: "#EF4444" }, flexShrink: 0 }}>
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
        </Gate>
      </Box>


      {/* Details */}
      {editingDetails ? (
        <TextField
          autoFocus
          fullWidth
          multiline
          size="small"
          value={detailsText}
          onChange={(e) => setDetailsText(e.target.value)}
          onBlur={() => {
            setEditingDetails(false);
            if (detailsText !== (data?.text || "") && onUpdate) {
              onUpdate(reminder.id, { text: detailsText });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.target.blur();
            }
            if (e.key === "Escape") {
              setDetailsText(data?.text || "");
              setEditingDetails(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="Add details..."
          sx={{ mt: 0.5, "& .MuiInputBase-root": { fontSize: 12, p: 0.5 } }}
        />
      ) : (
        <Typography
          onDoubleClick={canEditDetails ? (e) => { e.stopPropagation(); setEditingDetails(true); } : undefined}
          sx={{
            fontSize: 12, color: data?.text ? "#64748b" : "#9CA3AF", lineHeight: 1.5, mt: 0.5,
            cursor: "default", ...doneStyle,
          }}
        >
          {data?.text ? `Details: ${data.text}` : "Details: double-click to add"}
        </Typography>
      )}

    </Box>
  );
}

/* ── Main panel ── */

const PAGE_SIZE = 15;
const INBOX_FETCH_LIMIT = 100;
const GLOBAL_REF_COLLECTION = "global";

const isReminderDone = (r) => {
  const d = typeof r.data === "string" ? (() => { try { return JSON.parse(r.data); } catch { return {}; } })() : (r.data || {});
  return ["done", "completed"].includes(String(d?.status || "").trim().toLowerCase());
};
const isReminderFired = (r) => (r.state === "due" || r.status === "triggered") && !isReminderDone(r);

export default function RemindersPanel({
  authorId,
  refCollection,
  refId,
  onClose,
  refreshKey,
  selectedReminderId,
  onSelectReminder,
  // Inbox configuration: no ref scoping, fired-and-not-done only, most
  // overdue first. Same component as the workspace panel, per the
  // one-implementation rule.
  inboxMode = false,
}) {
  const [reminders, setReminders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const { setPanels, closeReminderInbox } = useLayout();

  // Active domain for silent journal isolation (never rendered).
  const reduxDomain = useSelector((state) => state.auth.user?.domain || "");
  const scopeDomain = reduxDomain && reduxDomain !== "All" ? reduxDomain : "";

  // Resolve display labels for reminders whose ref points at a contact or a
  // property but whose data was never stamped with a name (regression window
  // + AppBar quick-create). Cached per ref id for the lifetime of the panel.
  const [refLabels, setRefLabels] = useState({});
  useEffect(() => {
    const need = reminders.filter((r) => {
      const d = typeof r.data === "string" ? (() => { try { return JSON.parse(r.data); } catch { return {}; } })() : (r.data || {});
      const coll = r.ref?.collection;
      return r.ref?.id && !d.contactName && (coll === "contacts" || coll === "propertyAssets") && refLabels[r.ref.id] === undefined;
    });
    if (!need.length) return;
    let cancelled = false;
    (async () => {
      const entries = [];
      // Contacts resolve through the domain-scoped LIST query (ids filter):
      // the single-contact query is author-scoped on the backend and cannot
      // see contacts created by other users in the same domain.
      const contactIds = [...new Set(need.filter((r) => r.ref.collection === "contacts").map((r) => r.ref.id))];
      if (contactIds.length) {
        try {
          const res = await apiGetContactsCollection({
            filters: { ids: contactIds, ...(scopeDomain ? { domain: scopeDomain } : {}) },
            pagination: { page: 1, limit: contactIds.length },
          });
          const byId = new Map((res?.items || []).map((c) => [c.id, c]));
          contactIds.forEach((id) => {
            const c = byId.get(id);
            const person = c?.identity?.person || {};
            const name = [person.firstName, person.lastName].filter(Boolean).join(" ") || c?.identity?.company?.name || "";
            entries.push([id, name]);
          });
        } catch {
          contactIds.forEach((id) => entries.push([id, ""]));
        }
      }
      const propRefs = need.filter((r) => r.ref.collection === "propertyAssets");
      await Promise.all(propRefs.map(async (r) => {
        try {
          const res = await apiGetPropertyAsset(r.ref.id);
          const prop = res?.propertyAsset || res;
          const a = prop?.identity?.address || {};
          const label = [[a.streetNo, a.streetName].filter(Boolean).join(" "), a.city].filter(Boolean).join(", ");
          entries.push([r.ref.id, label]);
        } catch {
          entries.push([r.ref.id, ""]);
        }
      }));
      if (!cancelled) setRefLabels((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    })();
    return () => { cancelled = true; };
  }, [reminders, authorId, refLabels, scopeDomain]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchReminders = useCallback(async (pageNum) => {
    if (!authorId) return;
    setLoading(true);
    try {
      const res = await apiGetJournals({
        authorId,
        type: "reminder",
        sort: "dueDate",
        offset: inboxMode ? 0 : (pageNum - 1) * PAGE_SIZE,
        limit: inboxMode ? INBOX_FETCH_LIMIT : PAGE_SIZE,
        ...(!inboxMode && refCollection ? { refCollection } : {}),
        ...(!inboxMode && refId ? { refId } : {}),
        ...(scopeDomain ? { domain: scopeDomain } : {}),
      });
      const items = res?.journals || [];

      if (inboxMode) {
        // Inbox: only fired-and-not-done reminders, most overdue first. The
        // AppBar badge uses the same filter, so count and contents agree.
        const fired = items.filter(isReminderFired).sort((a, b) => {
          const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          return aDate - bDate;
        });
        setTotal(fired.length);
        setReminders(fired);
      } else {
        setTotal(res?.total || 0);
        // Sort: due reminders on top, then by dueDate ascending (soonest first)
        const sorted = [...items].sort((a, b) => {
          const aDue = a.state === "due" || a.status === "triggered";
          const bDue = b.state === "due" || b.status === "triggered";
          const aDone = isReminderDone(a);
          const bDone = isReminderDone(b);

          // Done items go to the bottom
          if (aDone !== bDone) return aDone ? 1 : -1;
          // Due items go to the top
          if (aDue !== bDue) return aDue ? -1 : 1;
          // Then sort by dueDate ascending (soonest first)
          const aDate = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
          const bDate = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
          return aDate - bDate;
        });
        setReminders(sorted);
      }
    } catch (err) {
      console.error("Failed to fetch reminders:", err);
    }
    setLoading(false);
  }, [authorId, refCollection, refId, scopeDomain, inboxMode]);

  useEffect(() => {
    fetchReminders(page);
  }, [fetchReminders, page, refreshKey]);

  const handleDelete = useCallback(async (id) => {
    try {
      await apiDeleteJournal(id);
      fetchReminders(page);
    } catch (err) {
      console.error("Failed to delete reminder:", err);
    }
  }, [fetchReminders, page]);

  const handleMarkDone = useCallback(async (id) => {
    try {
      await apiCancelJournalReminder(id);
      const reminder = reminders.find((r) => r.id === id);
      const existData = typeof reminder?.data === "string" ? JSON.parse(reminder.data) : (reminder?.data || {});
      const doneData = { ...existData, status: "done" };
      await apiUpdateJournal({ id, data: doneData });
      setReminders((prev) => prev.map((r) => {
        if (r.id !== id) return r;
        return { ...r, state: "due", agendaJobId: "", data: doneData };
      }));
    } catch (err) {
      console.error("Failed to mark reminder as done:", err);
    }
  }, [reminders]);

  const handleUpdateReminder = useCallback(async (id, updates) => {
    try {
      const reminder = reminders.find((r) => r.id === id);
      if (!reminder) return;
      const updateInput = { id };
      if (updates.dueDate) {
        updateInput.dueDate = updates.dueDate;
      }
      if (updates.text !== undefined) {
        const existingData = typeof reminder.data === "string"
          ? JSON.parse(reminder.data) : (reminder.data || {});
        updateInput.data = { ...existingData, text: updates.text };
      }
      await apiUpdateJournal(updateInput);
      fetchReminders(page);
    } catch (err) {
      console.error("Failed to update reminder:", err);
    }
  }, [reminders, fetchReminders, page]);

  const dueCount = reminders.filter(
    (r) => (r.state === "due" || r.status === "triggered") &&
      !["done", "completed"].includes(String(r.data?.status || "").trim().toLowerCase())
  ).length;

  // Jump from a reminder straight to a panel on its linked entity (journal
  // or comments) — the follow-up lives where all the entity's other notes
  // live. Global reminders open the standalone journal.
  const openEntityPanel = useCallback((reminder, panelUpdates, { keepInbox = false } = {}) => {
    const coll = reminder?.ref?.collection;
    const id = reminder?.ref?.id;
    if (!keepInbox) closeReminderInbox();
    const suffix = scopeDomain || "all";
    if (coll === "contacts" && id) {
      try { window.localStorage.setItem(`contextstory.addressbook.selectedContactId:${suffix}`, id); } catch { /* selection restore is best-effort */ }
      setPanels(panelUpdates);
      if (window.location.pathname.startsWith("/addressbook")) {
        window.dispatchEvent(new CustomEvent(SELECT_ENTITY_JOURNAL_EVENT, { detail: { collection: coll, id } }));
      } else {
        navigate("/addressbook");
      }
    } else if (coll === "propertyAssets" && id) {
      try { window.localStorage.setItem(`contextstory.properties.selectedPropertyId:${suffix}`, id); } catch { /* selection restore is best-effort */ }
      setPanels(panelUpdates);
      if (window.location.pathname.startsWith("/properties")) {
        window.dispatchEvent(new CustomEvent(SELECT_ENTITY_JOURNAL_EVENT, { detail: { collection: coll, id } }));
      } else {
        navigate("/properties");
      }
    } else if (panelUpdates.journal) {
      navigate("/journal");
    }
  }, [closeReminderInbox, scopeDomain, setPanels, navigate]);

  const openEntityJournal = useCallback((reminder) => {
    openEntityPanel(reminder, { journal: true });
  }, [openEntityPanel]);

  // Call / Text / Email on a reminder card: show a scannable QR (with the
  // number or address in large text) and surface the contact's comments
  // section behind it. The inbox stays open under the QR; it closes with it.
  const [qrAction, setQrAction] = useState(null); // { value, display, name }
  const handleCardAction = useCallback((reminder, kind, info) => {
    if (kind === "email" && info?.email) {
      // Email composes in the user's default mail client — no QR needed.
      window.location.href = `mailto:${info.email}`;
      openEntityPanel(reminder, { comments: true, details: false, chat: false });
      return;
    }
    if (kind === "call" && info?.phone) setQrAction({ value: `tel:${info.phone}`, display: info.phone, name: info.name });
    else if (kind === "text" && info?.phone) setQrAction({ value: `sms:${info.phone}`, display: info.phone, name: info.name });
    else return;
    openEntityPanel(reminder, { comments: true, details: false, chat: false }, { keepInbox: true });
  }, [openEntityPanel]);
  const closeQrAction = useCallback(() => {
    setQrAction(null);
    if (inboxMode) closeReminderInbox();
  }, [inboxMode, closeReminderInbox]);

  // Done-plus-note in one flow: complete the reminder, then land in the
  // entity's journal ready to log what happened.
  const completeWithNote = useCallback(async (reminder) => {
    await handleMarkDone(reminder.id);
    openEntityJournal(reminder);
  }, [handleMarkDone, openEntityJournal]);

  return (
    <AddressbookPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <ReminderIcon sx={{ fontSize: 16, color: "#D97706" }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
            {inboxMode ? "Reminder Inbox" : "Reminders"}
          </Typography>
          {dueCount > 0 && (
            <Box sx={{
              ml: 0.5, px: 0.75, py: 0.125, borderRadius: "999px",
              backgroundColor: "#FDE68A", color: "#92400E",
              fontSize: 11, fontWeight: 700, lineHeight: 1.4,
            }}>
              {dueCount} due
            </Box>
          )}
        </Box>
      }
      headerActions={
        <Gate flag="reminders.createGlobal">
        <IconButton
          size="small"
          title="Create reminder"
          onClick={() => setCreateOpen(true)}
          sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
        >
          <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
        </IconButton>
        </Gate>
      }
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {total} reminder{total !== 1 ? "s" : ""}
        </Typography>
      }
      footerRight={
        !inboxMode && totalPages > 1 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              sx={{ width: 22, height: 22, p: 0 }}>
              <PrevIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography sx={{ fontSize: 11, color: "text.secondary", minWidth: 40, textAlign: "center" }}>
              {page}/{totalPages}
            </Typography>
            <IconButton size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
              sx={{ width: 22, height: 22, p: 0 }}>
              <NextIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        ) : null
      }
    >
      {/* Defaults: an explicit ref prop wins; otherwise the page context
          decides (contact page → selected contact, property page → selected
          property, elsewhere → global). */}
      {(() => {
        const ctx = refId && refCollection
          ? { mode: refCollection === "contacts" ? "contact" : "property", ref: { collection: refCollection, id: refId } }
          : getReminderContextDefaults(reduxDomain);
        return (
          <ReminderCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            authorId={authorId}
            domain={reduxDomain}
            defaultMode={ctx.mode}
            defaultRef={ctx.ref}
            onCreated={() => { setPage(1); fetchReminders(1); }}
          />
        );
      })()}
      {/* Scannable action QR: phone/email rendered large under the code. */}
      <Dialog open={Boolean(qrAction)} onClose={closeQrAction} maxWidth="xs">
        {qrAction && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.25, p: 3 }}>
            {qrAction.name && (
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>{qrAction.name}</Typography>
            )}
            <QRCodeSVG value={qrAction.value} size={200} />
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1e293b", letterSpacing: "0.02em", wordBreak: "break-all", textAlign: "center" }}>
              {qrAction.display}
            </Typography>
          </Box>
        )}
      </Dialog>
      {loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : reminders.length === 0 ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>No reminders</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 0.75 }}>
          {reminders.map((r) => (
            <ReminderCard key={r.id} reminder={r} refLabel={refLabels[r.ref?.id] || ""} selected={r.id === selectedReminderId} onSelect={onSelectReminder} onDelete={() => handleDelete(r.id)} onMarkDone={() => handleMarkDone(r.id)} onUpdate={handleUpdateReminder} onOpenEntity={openEntityJournal} onCompleteWithNote={completeWithNote} onAction={handleCardAction} />
          ))}
        </Box>
      )}
    </AddressbookPanelShell>
  );
}
